/**
 * ECR Agent client (MenuX browser side)
 * =====================================
 * MenuX does NOT talk to the N950S directly.
 * It talks to the ECR agent on the same PC.
 *
 * Same pattern as: services/localPrintAgent.ts (printers on port 9100)
 * This file uses port 9200 by default.
 */

import { getEcrAgentUrl } from 'helpers/offlineTerminal/offlineTerminalConfig';
import type {
	OfflineTerminalInquiryResponse,
	OfflineTerminalPayRequest,
	OfflineTerminalPayResponse,
	OfflineTerminalVoidRequest,
	OfflineTerminalVoidResponse,
} from 'helpers/offlineTerminal/offlineTerminalTypes';

const BASE_URL = getEcrAgentUrl();

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
	const res = await fetch(`${BASE_URL}${path}`, {
		...init,
		headers: { 'Content-Type': 'application/json', ...init?.headers },
	});

	const text = await res.text();
	let data: T;
	try {
		data = text ? JSON.parse(text) : ({} as T);
	} catch {
		throw new Error(`ECR Agent ${path}: invalid JSON — ${text}`);
	}

	if (!res.ok) {
		const message =
			(data as { responseMessage?: string })?.responseMessage ||
			`${res.status} ${res.statusText}`;
		throw new Error(`ECR Agent ${path}: ${message}`);
	}

	return data;
}

/** Check if the ECR agent is running */
export async function getEcrAgentHealth(): Promise<{
	status: string;
	service?: string;
	mockMode?: boolean;
}> {
	return fetchJson('/health');
}

/**
 * Step 3 in your flow diagram: send payment to terminal.
 * Customer completes payment on N950S; we get the result back.
 */
export async function payOnTerminal(
	request: OfflineTerminalPayRequest,
): Promise<OfflineTerminalPayResponse> {
	return fetchJson<OfflineTerminalPayResponse>('/pay', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}

/** Void a payment on the terminal before voiding in MenuX */
export async function voidOnTerminal(
	request: OfflineTerminalVoidRequest,
): Promise<OfflineTerminalVoidResponse> {
	return fetchJson<OfflineTerminalVoidResponse>('/void', {
		method: 'POST',
		body: JSON.stringify(request),
	});
}

/** Check if a payment actually went through (Retry / Inquiry flow) */
export async function inquiryTerminalTransaction(
	transactionId: string,
	outletId?: string,
): Promise<OfflineTerminalInquiryResponse> {
	return fetchJson<OfflineTerminalInquiryResponse>('/inquiry', {
		method: 'POST',
		body: JSON.stringify({ transactionId, outletId }),
	});
}
